import {useEffect, useMemo, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import {arrayMove} from '@dnd-kit/sortable';
import Util from '../../shared/util/util';
import ReorderProjectStagesMutation from '../../../mutations/ReorderProjectStagesMutation';
import {createToast} from '../../shared/components/toasts/another-toast/toaster';
import {isStageOrderOutOfDate} from './projectStageValidation';
import type {ProjectStageRow} from './SettingsProjectStages';

export type ProjectStageOrder = {
	ordered: ReadonlyArray<ProjectStageRow>;
	saving: boolean;
	moveStage: (activeId: string, overId: string) => void;
};

const bySortOrder = (a: ProjectStageRow, b: ProjectStageRow) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0);

/**
 * The Project Stages list in display order, and moving a stage within it. A move shows at once and
 * saves the complete order; until the save settles, further moves are ignored. On success the stored
 * order takes over; on failure the previous order comes back.
 */
export const useProjectStageOrder = (
	stages: ReadonlyArray<ProjectStageRow>,
	refetchStages: () => Promise<unknown>
): ProjectStageOrder => {
	const intl = useIntl();
	const [pending, setPending] = useState<ReadonlyArray<ProjectStageRow> | null>(null);
	const [saving, setSaving] = useState(false);
	const mounted = useRef(true);
	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);

	const stored = useMemo(() => [...stages].sort(bySortOrder), [stages]);
	const ordered = pending ?? stored;

	const finish = () => {
		if (mounted.current) {
			setPending(null);
			setSaving(false);
		}
	};

	// Saving only ends on a path that really ends it: the default handler returns true when it has
	// scheduled a retry, and the handles stay disabled until that attempt settles.
	const handleError = (error: Error, onFailure: (error: Error) => boolean | void) => {
		if (!mounted.current) {
			onFailure(error);
			return;
		}
		if (isStageOrderOutOfDate(error)) {
			// The toast says the list was reloaded, so it waits for the reload; either way the local order goes.
			refetchStages()
				.then(() =>
					createToast({
						duration: 5000,
						message: intl.formatMessage({id: 'settings.project_stages.error.order_out_of_date'}),
					})
				)
				.catch(() => undefined)
				.then(finish);
			return;
		}
		if (onFailure(error) !== true) {
			finish();
		}
	};

	const moveStage = (activeId: string, overId: string) => {
		if (saving || activeId === overId) {
			return;
		}
		const from = ordered.findIndex(stage => stage.id === activeId);
		const to = ordered.findIndex(stage => stage.id === overId);
		if (from < 0 || to < 0) {
			return;
		}
		const next = arrayMove([...ordered], from, to);
		setPending(next);
		setSaving(true);
		Util.CommitMutation(ReorderProjectStagesMutation, {ids: next.map(stage => stage.id)}, finish, true, null, handleError);
	};

	return {ordered, saving, moveStage};
};
